import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

const PRINCIPLES = [
  { icon: "ri-search-eye-line", title: "去伪存真", desc: "以公开、可核实的节目物料与行业信息为准，整理时不渲染、不夸大，尽量呈现事件的真实脉络。" },
  { icon: "ri-stack-fill", title: "按需聚合", desc: "围绕热搜与用户兴趣，把零散的片段、帖子和讨论聚合成分门别类的合集，方便快速浏览。" },
  { icon: "ri-links-fill", title: "来源可溯", desc: "视频物料尽可能直连官方原片，引用信息标注出处，让每一条瓜都能追得到源头。" },
  { icon: "ri-mental-health-fill", title: "理性吃瓜", desc: "倡导克制、友善的讨论氛围，不站队、不网暴，让吃瓜回归信息获取与轻松交流本身。" },
];

const VOICES = [
  { name: "追更小圆", role: "综艺爱好者", text: "每天中午刷一眼热榜就把节目补了，名场面盘点真的救了我这种总错过更新的人。" },
  { name: "北屿不加班", role: "通勤党", text: "合集按热度排得很清楚，半小时通勤刚好能读完一篇精读，节奏刚刚好。" },
  { name: "Cee的电影票根", role: "影视迷", text: "从“烧脑”到“共情”那篇读得很有共鸣，能把行业观察写得这么顺滑不多见。" },
];

export default function AboutPage() {
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://wind-in-soul.cn/" },
      { "@type": "ListItem", position: 2, name: "关于我们" },
    ],
  };

  const aboutLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于海角吃瓜",
    url: "https://wind-in-soul.cn/about",
    about: { "@type": "Organization", name: "海角吃瓜" },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutLd) }} />
      <Header />
      <main className="w-full bg-background-50">
        {/* 顶部 */}
        <section className="w-full overflow-hidden bg-background-100">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16 lg:px-12">
            <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
            <div className="mt-8 max-w-3xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
                <i className="ri-sailboat-fill" aria-hidden="true"></i>
                海角吃瓜 · About
              </span>
              <h1 className="mt-4 font-heading text-4xl font-black leading-tight text-foreground-950 md:text-5xl">
                在娱乐的海岸线上
                <br />
                帮你把瓜吃得明明白白
              </h1>
              <p className="mt-5 text-base leading-relaxed text-foreground-600">
                海角吃瓜是一处按热度聚合圈内新鲜事与高赞讨论的娱乐内容站点。我们从海量信息里打捞值得看的部分，用合集与盘点帮你轻松补上每一次错过的热闹，也陪你理性地聊完每一场讨论。
              </p>
            </div>
          </div>
        </section>

        {/* 定位与理念 */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 gap-10 px-4 md:px-8 lg:grid-cols-12 lg:px-12">
            <div className="lg:col-span-5">
              <div className="sticky top-24">
                <p className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                  <i className="ri-compass-3-line" aria-hidden="true"></i>
                  我们是谁
                </p>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  定位与理念
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  娱乐圈的信息更新快得像涨潮，内容散落在不同平台、不同帖子里。我们做的，就是把这股信息流收集起来、按热度梳理成可读的合集与盘点，让你用更短的时间，追到更有价值的内容。
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  我们相信，吃瓜不是低俗的围观，而是一种普通人了解大众文化、参与公共讨论的日常方式。它值得被更清爽、更克制、更真诚地对待。
                </p>
                <div className="mt-6 grid grid-cols-3 gap-4">
                  {[
                    { n: "4", label: "大主题频道" },
                    { n: "每日", label: "多次滚动更新" },
                    { n: "实时", label: "热度排行" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-2xl border border-background-200/70 bg-background-100 p-4 text-center">
                      <p className="font-heading text-2xl font-black text-primary-600">{s.n}</p>
                      <p className="mt-1 text-xs text-foreground-500">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {PRINCIPLES.map((p, i) => (
                  <article
                    key={p.title}
                    className="rounded-2xl border border-background-200/70 bg-background-100 p-6 transition-colors hover:border-accent-300"
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                        <i className={`${p.icon} text-xl`} aria-hidden="true"></i>
                      </span>
                      <span className="font-heading text-4xl font-black text-secondary-300">0{i + 1}</span>
                    </div>
                    <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 用户口碑 */}
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="mx-auto w-full max-w-[1200px] px-4 md:px-8 lg:px-12">
            <div className="text-center">
              <p className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                <i className="ri-chat-heart-fill" aria-hidden="true"></i>
                瓜友有话说
              </p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                大家的真实反馈
              </h2>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {VOICES.map((v) => (
                <figure
                  key={v.name}
                  className="flex flex-col rounded-2xl border border-background-200/70 bg-background-50 p-6"
                >
                  <i className="ri-double-quotes-l text-3xl text-accent-400" aria-hidden="true"></i>
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                    “{v.text}”
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200/70 pt-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-200 font-heading text-sm font-bold text-secondary-900">
                      {v.name[0]}
                    </span>
                    <span>
                      <p className="text-sm font-semibold text-foreground-900">{v.name}</p>
                      <p className="text-xs text-foreground-500">{v.role}</p>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 联系方式 CTA */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="mx-auto w-full max-w-[1200px] px-4 md:px-8 lg:px-12">
            <div className="relative overflow-hidden rounded-3xl bg-primary-900 px-6 py-12 text-center md:px-12 md:py-16">
              <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-primary-500/30 blur-2xl"></div>
              <div className="pointer-events-none absolute -bottom-12 -left-8 h-44 w-44 rounded-full bg-accent-500/20 blur-2xl"></div>
              <div className="relative">
                <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                  想投稿、纠错或聊聊合作？
                </h2>
                <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-primary-100/90">
                  我们珍视每一位瓜友的反馈。无论是补充资料、指出错误，还是分享你看到的有趣话题，都欢迎写信告诉我们，我们一起把内容做得更扎实。
                </p>
                <a
                  href="mailto:hello@haijiao.example"
                  className="mt-7 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-7 py-3 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-mail-send-line" aria-hidden="true"></i>
                  hello@haijiao.example
                </a>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs text-primary-200/80">
                  <span className="rounded-full bg-background-50/10 px-3 py-1 whitespace-nowrap">内容合作</span>
                  <span className="rounded-full bg-background-50/10 px-3 py-1 whitespace-nowrap">信息纠错</span>
                  <span className="rounded-full bg-background-50/10 px-3 py-1 whitespace-nowrap">话题投稿</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}